import React, { useEffect, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Switch, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { History, Plus, Trash2 } from "lucide-react";
import { hd } from "../api";
import { useCrud } from "./common";
import { Section } from "./common";
import { T, TYPE, formatDateTime, labelSx, primaryButtonSx } from "../../user/complaints/shared";

const th = { "& th": { ...labelSx, bgcolor: T.surfaceHeader } };
const EVENTS = ["*", "ticket.created", "ticket.updated", "ticket.resolved", "ticket.closed", "ticket.reopened", "conversation.created", "message.created", "chat.started", "chat.ended", "sla.warning", "sla.breached", "ai.handoff", "email.received", "email.sent"];

export const WebhooksEditor = () => {
  const { rows, create, update, remove } = useCrud("/webhooks");
  const [edit, setEdit] = useState(null);
  const [secret, setSecret] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  if (!rows) return <CircularProgress size={22} />;
  const save = async () => {
    const r = await create({ name: edit.name, url: edit.url, events: edit.events, enabled: true });
    if (r) {
      setEdit(null);
      if (r.secret) setSecret(r.secret);
    }
  };
  return (
    <Box>
      <Section title="Outgoing webhooks" description="JSON POSTs signed with HMAC-SHA256 in the X-JustWalk-Signature header (sha256=<hex> of the raw body). Failed deliveries retry up to 5 times."
        action={<Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={15} />} onClick={() => setEdit({ name: "", url: "https://", events: ["ticket.created"] })}>Add webhook</Button>}>
        {secret && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSecret(null)}>Signing secret (shown once): <b style={{ fontFamily: "monospace" }}>{secret}</b></Alert>}
        <Table size="small">
          <TableHead><TableRow sx={th}><TableCell>Name</TableCell><TableCell>URL</TableCell><TableCell>Events</TableCell><TableCell>Secret</TableCell><TableCell>On</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {rows.map((w) => (
              <TableRow key={w._id}>
                <TableCell sx={{ fontWeight: 600 }}>{w.name}</TableCell>
                <TableCell sx={{ fontSize: TYPE.micro, wordBreak: "break-all" }}>{w.url}</TableCell>
                <TableCell>{w.events.map((e) => <Chip key={e} size="small" label={e} sx={{ mr: 0.5, mb: 0.5 }} />)}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: TYPE.micro }}>{w.secretPreview}</TableCell>
                <TableCell><Switch size="small" checked={w.enabled} onChange={(e) => update(w._id, { enabled: e.target.checked })} /></TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <IconButton size="small" onClick={() => hd.webhookDeliveries(w._id).then((d) => setDeliveries({ w, d }))}><History size={15} /></IconButton>
                  <IconButton size="small" onClick={() => remove(w._id)}><Trash2 size={15} color="#B42318" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={6} sx={{ color: T.inkMuted }}>No webhooks</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Section>
      <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mt: 1 }}>WhatsApp is set up under Support Settings → WhatsApp.</Typography>
      {edit && (
        <Dialog open onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
          <DialogTitle>New webhook</DialogTitle>
          <DialogContent dividers>
            <TextField fullWidth label="Name" value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} sx={{ mb: 2 }} />
            <TextField fullWidth label="URL" value={edit.url} onChange={(e) => setEdit((p) => ({ ...p, url: e.target.value }))} sx={{ mb: 2 }} helperText="Private / internal network addresses are blocked" />
            <Autocomplete multiple options={EVENTS} value={edit.events} onChange={(_, v) => setEdit((p) => ({ ...p, events: v }))} renderInput={(p) => <TextField {...p} label="Events (* = all)" />} />
          </DialogContent>
          <DialogActions><Button onClick={() => setEdit(null)}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} onClick={save}>Create</Button></DialogActions>
        </Dialog>
      )}
      {deliveries && (
        <Dialog open onClose={() => setDeliveries(null)} maxWidth="md" fullWidth>
          <DialogTitle>Deliveries — {deliveries.w.name}</DialogTitle>
          <DialogContent dividers>
            <Table size="small">
              <TableHead><TableRow sx={th}><TableCell>When</TableCell><TableCell>Event</TableCell><TableCell>Status</TableCell><TableCell>HTTP</TableCell><TableCell>Attempts</TableCell></TableRow></TableHead>
              <TableBody>
                {deliveries.d.map((x) => <TableRow key={x._id}><TableCell>{formatDateTime(x.createdAt)}</TableCell><TableCell>{x.event}</TableCell><TableCell>{x.status}{x.error ? ` — ${x.error}` : ""}</TableCell><TableCell>{x.responseStatus ?? "—"}</TableCell><TableCell>{x.attempts}</TableCell></TableRow>)}
                {!deliveries.d.length && <TableRow><TableCell colSpan={5} sx={{ color: T.inkMuted }}>No deliveries yet</TableCell></TableRow>}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions><Button onClick={() => setDeliveries(null)}>Close</Button></DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export const AuditLog = () => {
  const [rows, setRows] = useState([]);
  const [done, setDone] = useState(false);
  const [action, setAction] = useState("");
  const load = async (reset) => {
    const before = reset ? undefined : rows[rows.length - 1]?._id;
    const r = await hd.audit({ before, action: action || undefined, limit: 50 });
    setRows((x) => (reset ? r : [...x, ...r]));
    setDone(r.length < 50);
  };
  useEffect(() => {
    load(true);
  }, [action]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Box>
      <TextField size="small" label="Filter by action (e.g. ticket.assigned)" value={action} onChange={(e) => setAction(e.target.value)} sx={{ mb: 1.5, minWidth: 320 }} />
      <Table size="small">
        <TableHead><TableRow sx={th}><TableCell>When</TableCell><TableCell>Who</TableCell><TableCell>Action</TableCell><TableCell>Details</TableCell></TableRow></TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r._id}>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(r.createdAt)}</TableCell>
              <TableCell>{r.actor?.name || r.actor?.kind}</TableCell>
              <TableCell sx={{ fontFamily: "monospace", fontSize: TYPE.micro }}>{r.action}</TableCell>
              <TableCell sx={{ fontSize: TYPE.micro, color: T.inkSecondary, maxWidth: 420 }}><Typography noWrap sx={{ fontSize: TYPE.micro }}>{r.after ? JSON.stringify(r.after) : r.meta ? JSON.stringify(r.meta) : ""}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!done && <Button sx={{ mt: 1 }} onClick={() => load(false)}>Load more</Button>}
    </Box>
  );
};
